import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Table, Typography, message, Spin, Modal, Input, Button, DatePicker, Space } from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function Otapplication() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // Format date as dd/mm/yyyy
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Format datetime as dd/mm/yyyy HH:mm:ss
    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};

            // 🔥 Add date filters if selected
            if (dateRange && dateRange.length === 2) {
                params.from_date = dayjs(dateRange[0]).format("YYYY-MM-DD");
                params.to_date = dayjs(dateRange[1]).format("YYYY-MM-DD");
            }

            const res = await axios.get(
                `${backUrl}/api/payroll/ot-eligible-days/`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                    params, // ✅ backend query params
                }
            );

            const formatted = (res.data.results || res.data).map((item) => ({
                ...item,
                approval_logs: item.approval_logs || [],
            }));

            setData(formatted);
            setFilteredData(formatted);
            setPagination((prev) => ({
                ...prev,
                total: formatted.length,
            }));
        } catch (err) {
            message.error("Failed to fetch OT data");
            console.error("Error fetching OT eligible days:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.access) {
            fetchData();
        }
    }, [authData, dateRange]);

    // Filter data by date range
    useEffect(() => {
        if (!dateRange) {
            setFilteredData(data);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: data.length,
            }));
            return;
        }

        const [startDate, endDate] = dateRange;
        const filtered = data.filter((item) => {
            const itemDate = dayjs(item.date, "YYYY-MM-DD");
            return itemDate.isSameOrAfter(startDate, "day") && itemDate.isSameOrBefore(endDate, "day");
        });

        setFilteredData(filtered);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: filtered.length,
        }));
    }, [dateRange, data]);

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
    };

    const handleClearFilter = () => {
        setDateRange(null);
    };

    const handleRequestOT = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        if (!reason.trim()) {
            message.warning("Please provide a reason before submitting");
            setSubmitting(false);
            return;
        }

        const payload = {
            attendance_record_id: selectedRecord.id,
            employee_remarks: reason,
            requested_ot_hours: selectedRecord.potential_ot_hours,
        };

        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/ot-requests/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("OT request submitted successfully!");
            setIsModalVisible(false);
            setReason("");
            fetchData();
        } catch (err) {
            console.error("Error submitting OT request:", err);

            const errorMessage =
                err.response?.data?.data ||
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.response?.data ||
                "Failed to submit OT request";

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const approvalColumns = [
        { title: "Order", dataIndex: "order", key: "order", align: "center" },
        { title: "Approver", dataIndex: "approver_name", key: "approver_name", align: "center" },
        { title: "Status", dataIndex: "status", key: "status", align: "center" },
        { title: "Remarks", dataIndex: "remarks", key: "remarks", align: "center" },
        {
            title: "Timestamp",
            dataIndex: "timestamp",
            key: "timestamp",
            align: "center",
            render: (text) => formatDateTime(text),
        },
    ];

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            align: "center",
            render: (text) => formatDate(text),
        },
        {
            title: "First Check-In",
            dataIndex: "first_check_in",
            key: "first_check_in",
            align: "center",
            render: (text) => formatDateTime(text),
        },
        {
            title: "Last Check-Out",
            dataIndex: "last_check_out",
            key: "last_check_out",
            align: "center",
            render: (text) => formatDateTime(text),
        },
        { title: "Total Time", dataIndex: "total_time", key: "total_time", align: "center" },
        { title: "Potential OT Hours", dataIndex: "potential_ot_hours", key: "potential_ot_hours", align: "center" },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record) =>
                record.ot_requested ? (
                    <p>OT Request Already Sent</p>
                ) : (
                    <Button
                        type="primary"
                        style={{ backgroundColor: "#0e497b" }}
                        onClick={() => handleRequestOT(record)}
                    >
                        Request OT
                    </Button>
                ),
        },
    ];

    return (
        <div style={{ margin: "30px" }}>
            <Title level={4} style={{ textAlign: "center", marginBottom: 20 }}>
                OT Eligible Days
            </Title>

            <Space style={{ marginBottom: 16 }}>
                <RangePicker
                    format="DD/MM/YYYY"
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder={["Start Date", "End Date"]}
                />
                <Button onClick={handleClearFilter}>Clear Filter</Button>
            </Space>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    bordered
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} records`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    onChange={handleTableChange}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ padding: "10px 30px" }}>
                                {record.approval_logs?.length > 0 ? (
                                    <Table
                                        columns={approvalColumns}
                                        dataSource={record.approval_logs}
                                        pagination={false}
                                        rowKey="id"
                                        size="small"
                                        bordered
                                    />
                                ) : (
                                    <p>No approval logs</p>
                                )}
                            </div>
                        ),
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            <Modal
                title="Request OT"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitting}
                        disabled={submitting}
                        style={{ backgroundColor: "#0e497b" }}
                        onClick={handleSubmit}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>,
                ]}
            >
                <p>
                    <strong>Date:</strong> {formatDate(selectedRecord?.date)}
                </p>
                <TextArea
                    rows={4}
                    placeholder="Enter reason for requesting OT"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}